"use client";

import { useCallback, useMemo, useState } from "react";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceArea,
  ReferenceLine,
  ReferenceDot,
  CartesianGrid,
  ResponsiveContainer,
  type MouseHandlerDataParam,
} from "recharts";
import type { PricePoint, YearValue } from "@/lib/data/types";
import {
  computeFairValueSeries,
  computeFairValueForecast,
  computeFairValueForecastBand,
  computeCurrentYearFairValue,
  computeIntervalPerformance,
  simulatePurchase,
  multipleRange,
  computeFairValueSeriesForMultiple,
  extrapolateFairValueForecast,
  type IntervalEndpoint,
} from "@/lib/valuation/fair-value";
import { FAIR_VALUE } from "@/lib/config";
import {
  computePriceAxisTicks,
  downsample,
  filterDateRange,
  formatPriceAxisTick,
  pricePrecisionDigits,
  withTimeField,
} from "@/lib/price-chart-utils";
import { RangeSelector, type ChartRangeLabel } from "@/components/charts/range-selector";
import { filterYearRange } from "@/lib/year-range";
import { averageOf } from "@/lib/stats";
import { perShareSeries } from "@/lib/valuation/multiples";
import { latestValue } from "@/lib/dividend";
import { formatCurrency, formatPercent, formatRatio } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { GlossaryTerm } from "@/components/glossary-term";
import { useIsNarrowViewport } from "@/lib/use-narrow-viewport";
import { Info, RotateCcw } from "lucide-react";
import { Tooltip as InfoTooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type ValuationMetric = "eps" | "revenue";

/** Gleiche Pill-Optik wie RangeSelector (components/charts/range-selector.tsx), fuer die
 * 2-Wege-Bewertungskennzahl-Auswahl - kein neues visuelles Muster. */
function MetricToggle({
  value,
  onChange,
}: {
  value: ValuationMetric;
  onChange: (value: ValuationMetric) => void;
}) {
  const options: { key: ValuationMetric; label: string }[] = [
    { key: "eps", label: "Gewinn je Aktie" },
    { key: "revenue", label: "Umsatz je Aktie" },
  ];
  return (
    <div className="flex shrink-0 gap-1 rounded-full bg-muted p-1">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className={cn(
            "rounded-full px-3 py-1 text-xs font-medium transition-colors",
            value === o.key ? "bg-card text-ink shadow-sm" : "text-muted-foreground hover:text-ink",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const POSITIVE = "#3F7A5B";
const GOLD = "#C6A15B";
const NAVY = "#16265C";

/** Ziel-Gesamtzahl an Prognosejahren (echte Analystenschaetzung + Wachstums-Extrapolation
 * zusammen) - backlog.md -> [Punkt 51]. */
const FORECAST_TARGET_YEARS = 3;

/** Anzeigename je Chart-Serie, die als Klick-Basis fuer die Zwei-Punkt-Simulation
 * dienen kann (Feedback-Runde 19 B/16) - macht sichtbar, WELCHE Linie ein Klick
 * tatsaechlich getroffen hat (Kurs vs. eine der drei Fair-Value-artigen Linien),
 * da das pixelnaechste Ziel nicht immer die optisch naheliegende Linie ist. */
const SOURCE_LABELS = {
  close: "Kurs",
  fairValue: "Fair Value",
  fairValueConservative: "Konservativ",
  fairValueOptimistic: "Optimistisch",
  fairValueExtrapolated: "Fair Value (Extrapoliert)",
  analystTargetMean: "Analysten (Konsens)",
  analystTargetHigh: "Analysten (Obergrenze)",
  analystTargetLow: "Analysten (Untergrenze)",
} as const;

function formatTooltipDate(value: string | number): string {
  return new Date(value).toLocaleDateString("de-DE", { year: "numeric", month: "short", day: "2-digit" });
}

/** Die drei Fair-Value-artigen Serien-Keys - gemeinsam von der Chartdaten-Aufbereitung
 * UND dem Klick-Handler genutzt (siehe FAIR_VALUE_KEYS unten), damit beide nicht
 * auseinanderlaufen koennen. */
type FairValueKey = "fairValue" | "fairValueConservative" | "fairValueOptimistic" | "fairValueExtrapolated";
const FAIR_VALUE_KEYS: FairValueKey[] = [
  "fairValue",
  "fairValueConservative",
  "fairValueOptimistic",
  "fairValueExtrapolated",
];

/** Die drei Analysten-Kurszielinien-Keys (Feedback-Runde 19 B/16) - bewusst NICHT Teil
 * von FAIR_VALUE_KEYS: deren Interpolationsschleife wuerde den konstanten Zielwert auch
 * VOR/NACH der Prognosezone flach fortschreiben (siehe chartDataWithAnalystTargets),
 * hier nur fuer die Tooltip-Kandidatenauswahl gebraucht (die eigentlichen Werte kommen
 * bereits fertig aus chartDataWithAnalystTargets). */
type AnalystTargetKey = "analystTargetMean" | "analystTargetHigh" | "analystTargetLow";
const ANALYST_TARGET_KEYS: AnalystTargetKey[] = ["analystTargetMean", "analystTargetHigh", "analystTargetLow"];

function tooltipEntryLabel(dataKey: string, metric: ValuationMetric): string {
  switch (dataKey) {
    case "close":
      return "Kurs";
    case "fairValue":
      return "Fair Value";
    case "fairValueExtrapolated":
      return "Fair Value (Extrapoliert)";
    case "fairValueConservative":
      return `Fair Value (Min-${metric === "eps" ? "KGV" : "KUV"})`;
    case "fairValueOptimistic":
      return `Fair Value (Max-${metric === "eps" ? "KGV" : "KUV"})`;
    case "analystTargetMean":
      return "Analysten-Kursziel (Konsens)";
    case "analystTargetHigh":
      return "Analysten-Kursziel (Obergrenze)";
    case "analystTargetLow":
      return "Analysten-Kursziel (Untergrenze)";
    default:
      return dataKey;
  }
}

/** Ersetzt Recharts' eingebaute Tooltip-Darstellung, die standardmaessig alle Serien mit
 * einem Wert am selben Zeitpunkt gleichzeitig auflistet - zeigt stattdessen nur die
 * Serie, die `activeKey` (siehe hoveredKey in FairValueSimulator) gerade als
 * naechstgelegen markiert hat (Feedback-Runde 16 Punkt 6, 2026-07-22). */
function ActivePointTooltip({
  active,
  payload,
  label,
  activeKey,
  metric,
  priceFormatter,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ dataKey?: unknown; value?: unknown }>;
  label?: string | number;
  activeKey?: string;
  metric: ValuationMetric;
  priceFormatter: Intl.NumberFormat;
}) {
  if (!active || !payload || payload.length === 0 || label === undefined) return null;
  const candidates = payload.filter(
    (entry) =>
      entry.dataKey === "close" ||
      FAIR_VALUE_KEYS.includes(entry.dataKey as FairValueKey) ||
      ANALYST_TARGET_KEYS.includes(entry.dataKey as AnalystTargetKey),
  );
  const entry = candidates.find((e) => e.dataKey === activeKey) ?? candidates.find((e) => e.dataKey === "close");
  if (!entry || typeof entry.dataKey !== "string" || typeof entry.value !== "number") return null;

  return (
    <div
      style={{
        borderRadius: 4,
        border: "1px solid #ECEAE3",
        fontSize: 12,
        padding: "6px 10px",
        backgroundColor: "rgba(250, 249, 245, 0.92)",
        boxShadow: "0 4px 16px rgba(22,38,92,0.08)",
      }}
    >
      <p style={{ margin: 0, marginBottom: 4, color: "#6B7280" }}>{formatTooltipDate(Number(label))}</p>
      <p style={{ margin: 0, color: "#26407F" }}>
        {tooltipEntryLabel(entry.dataKey, metric)}: {priceFormatter.format(entry.value)}
      </p>
    </div>
  );
}

interface ChartRow {
  time: number;
  close?: number;
  fairValue?: number;
  /** Hilfslinien fuer alternative Fair-Value-Annahmen (Nutzer-Feedback 2026-07-18):
   * gleiche EPS-/Umsatz-Basis wie `fairValue`, aber mit dem niedrigsten/hoechsten
   * validen historischen Multiplikator statt dessen Durchschnitt - macht den
   * Zwei-Punkte-Vergleich auch fuer konservativere/optimistischere Annahmen nutzbar,
   * nicht nur fuer die eine Analysten-/Durchschnittslinie. */
  fairValueConservative?: number;
  fairValueOptimistic?: number;
  /** Wachstums-Extrapolation ueber die echten Analystenschaetzjahre hinaus (backlog.md ->
   * [Punkt 51]) - eigener Key statt in `fairValue` zu verschmelzen, damit die Grenze
   * zwischen "echte Schaetzung" und "Extrapolation" visuell (gestrichelt) UND strukturell
   * eindeutig bleibt, siehe extrapolateFairValueForecast(). */
  fairValueExtrapolated?: number;
  /** Prognosekorridor optimistisch/pessimistisch (backlog.md -> D1/Feature-2-Ergaenzung,
   * §B.2) - `forecastBase` ist die untere Bandgrenze, `forecastSpread` die Differenz zur
   * oberen Grenze (Stacked-Area-Technik). */
  forecastBase?: number;
  forecastSpread?: number;
  /** Feedback-Runde 19 B/16: die drei Analysten-Kurszielinien (Konsens/Ober-/
   * Untergrenze) waren bisher reine `ReferenceLine`-Elemente ohne `dataKey` - Klicks in
   * ihrer Naehe loesten immer nur die naechstgelegene der anderen 4 echten Serien aus,
   * eine Flaeche/Linie DAZWISCHEN war gar nicht klickbar. Konstante Werte (kein
   * Kurs, sondern Analysten-Konsensziele) innerhalb der Prognosezone, siehe
   * chartDataWithAnalystTargets - dieselbe "nächstgelegene Serie gewinnt"-Logik wie
   * bei den uebrigen Serien macht Klicks dazwischen automatisch moeglich. */
  analystTargetMean?: number;
  analystTargetHigh?: number;
  analystTargetLow?: number;
}

/**
 * Flagship Fairer-Wert & Kauf-Simulator (backlog.md -> §B) - massive Erweiterung von
 * Punkt 1 (computeFairValueSeries bleibt die Datengrundlage) und Punkt 7 (bleibt ein
 * eigenstaendiger, passiver Alarm daneben). "Werkzeug zum Denken": enthuellt die
 * historische Bewertungsrelation, trifft keine Kauf-/Verkaufsaussage.
 *
 * Freie Zwei-Punkt-Simulation (Klick auf zwei Chart-Punkte, "Zeitraum-Vergleich"-Panel)
 * sowie eine TTM-basierte Fair-Value-Bruecke fuers laufende Jahr sind seit 2026-07-14
 * umgesetzt (Erik-Auftrag). Seit 2026-07-14 (backlog.md -> D1/Feature-2-Ergaenzung §B.1/
 * §B.2) zusaetzlich: Bewertungszeitraum-Dropdown (welche Jahre in den Durchschnitts-
 * Multiplikator einfliessen, unabhaengig vom Chart-Anzeigezeitraum), Bewertungskennzahl-
 * Umschalter (Gewinn/Umsatz je Aktie), Log-Skala-Toggle, optimistischer/pessimistischer
 * Prognosekorridor (aus Yahoos earningsEstimate low/high). **Bewusste Begrenzung**: die
 * Bewertungskennzahl-Option ist auf Gewinn/Umsatz beschraenkt (nicht die urspruenglich
 * angefragten 5 Optionen inkl. Cashflow/FFO/bereinigt-vs-ausgewiesen) - fuer Cashflow
 * fehlt eine historische Kurs/FCF-Multiple-Zeitreihe, fuer FFO/bereinigten Gewinn fehlt
 * die Datengrundlage komplett (siehe Rueckfrage im Chat zur Kennzahl-Diskrepanz);
 * TTM-Bruecke und Prognosekorridor bleiben auf die Gewinn-Kennzahl beschraenkt (fuer
 * Umsatz fehlt ein trailing Umsatz-Wert bzw. eine Umsatz-Bandbreite). Der
 * Analysten-Kurszielpunkt wird nur gezeigt, wenn Yahoo tatsaechlich einen Konsens
 * liefert (kein erfundener Wert); die Prognosezone zeigt ehrlich nur so viele Jahre wie
 * die Datenquelle liefert (aktuell realistisch 1-2 Analystenjahre plus TTM-Bruecke,
 * kein erfundenes drittes Jahr - siehe computeFairValueForecast).
 */
export interface FairValueSimulatorProps {
  priceHistory: PricePoint[];
  pe: YearValue[];
  eps: YearValue[];
  revenue: YearValue[];
  sharesOutstanding: YearValue[];
  priceToSales: YearValue[];
  dividendPerShare: YearValue[];
  perYearEstimates: { year: number; eps?: number; epsLow?: number; epsHigh?: number; revenue?: number }[];
  /** Yahoos "+5y"-Gewinnwachstumsrate (AnalystEstimates.longTermGrowthRate) - Grundlage
   * fuer die Prognose-Extrapolation ueber die echten Analystenschaetzjahre hinaus (backlog.md
   * -> [Punkt 51], gleiche Datenquelle/gleiches Prinzip wie beim Nachbar-Chart
   * "Bewertungskennzahlen im Verlauf", siehe computeForwardMultiplePath in
   * lib/valuation/multiples.ts). Nur fuer die Gewinn-Kennzahl nutzbar (fuer Umsatz fehlt
   * eine eigene Umsatzwachstumsrate). */
  longTermGrowthRate?: number;
  priceTargetMean?: number;
  /** Echte Analysten-Kurszielextremwerte (Yahoo targetHighPrice/targetLowPrice, siehe
   * AnalystEstimates.priceTarget in lib/data/types.ts) - backlog.md -> "Offene Features/
   * Bugs": Datenverfuegbarkeit geprueft, echte Werte sind vorhanden, daher hier verdrahtet
   * statt nur die bestehenden Konservativ-/Optimistisch-Linien (die auf der EIGENEN
   * historischen KGV-Bandbreite beruhen, nicht auf Analystenmeinungen) umzubenennen. */
  priceTargetHigh?: number;
  priceTargetLow?: number;
  currentPrice?: number;
  currentPe?: number;
  /** Trailing-Twelve-Months-EPS (Yahoo `trailingEps`) - Bruecke fuers laufende Jahr. */
  currentEps?: number;
  currentPriceToSales?: number;
  /** Keine Zeitreihe verfuegbar (siehe kpi-config.ts), daher nur als "Akt."-Zeile in der
   * Multiples-Tabelle nutzbar, nicht als eigener Schnitt/Chart-Wert (backlog.md ->
   * Feedback-Runde 2026-07-17, Punkt 12). */
  currentEvToEbitda?: number;
  currency?: string;
  /** Gemeinsamer Zeitraum-State mit DailyMultiplesChart (siehe ValuationTabCharts) - vom
   * Elternteil kontrolliert statt eines eigenen internen States, damit im Bewertung-Tab
   * nur noch EIN sichtbarer RangeSelector statt zweier nebeneinander existiert (Erik-
   * Feedback 2026-07-21: "doppelte Zeitraum-Auswahl"). */
  range: ChartRangeLabel;
  onRangeChange: (range: ChartRangeLabel) => void;
}

export function FairValueSimulator({
  priceHistory,
  pe,
  eps,
  revenue,
  sharesOutstanding,
  priceToSales,
  dividendPerShare,
  perYearEstimates,
  longTermGrowthRate,
  priceTargetMean,
  priceTargetHigh,
  priceTargetLow,
  currentPrice,
  currentPe,
  currentEps,
  currentPriceToSales,
  currentEvToEbitda,
  currency = "USD",
  range,
  onRangeChange,
}: FairValueSimulatorProps) {
  const valuationRange = range;
  const [metric, setMetric] = useState<ValuationMetric>("eps");
  const [logScale, setLogScale] = useState(false);
  const [showFairValue, setShowFairValue] = useState(true);
  const [amountDraft, setAmountDraft] = useState("1000");
  // Editierbare KGV/KUV-Annahme (backlog.md -> [Punkt 48], Vorbild Aktienfinder): Draft
  // als String wie amountDraft oben (erlaubt uebergangsweise leere/unfertige Eingabe beim
  // Tippen), geparst erst beim tatsaechlichen Gebrauch unten. undefined/leer/ungueltig
  // faellt komplett auf die echte historische Durchschnittslinie zurueck.
  const [customMultipleDraft, setCustomMultipleDraft] = useState("");

  // Umsatz je Aktie ist keine direkt gelieferte Zeitreihe - aus Umsatz + Aktienanzahl
  // desselben Jahres hergeleitet (backlog.md -> D1/Feature-2-Ergaenzung).
  const revenuePerShare = useMemo(() => perShareSeries(revenue, sharesOutstanding), [revenue, sharesOutstanding]);

  // Bewertungszeitraum filtert NUR, welche Jahre in den Durchschnitts-Multiplikator
  // einfliessen - unabhaengig vom `range`-State oben, der nur die Chart-ANZEIGE steuert.
  const filteredPe = useMemo(() => filterYearRange(pe, valuationRange), [pe, valuationRange]);
  const filteredEps = useMemo(() => filterYearRange(eps, valuationRange), [eps, valuationRange]);
  const filteredPriceToSales = useMemo(
    () => filterYearRange(priceToSales, valuationRange),
    [priceToSales, valuationRange],
  );
  const filteredRevenuePerShare = useMemo(
    () => filterYearRange(revenuePerShare, valuationRange),
    [revenuePerShare, valuationRange],
  );

  const filteredMultiple = metric === "eps" ? filteredPe : filteredPriceToSales;
  const filteredMetricSeries = metric === "eps" ? filteredEps : filteredRevenuePerShare;
  const fullMultiple = metric === "eps" ? pe : priceToSales;
  const fullMetricSeries = metric === "eps" ? eps : revenuePerShare;

  const fairValueForRange = useMemo(
    () => computeFairValueSeries(filteredMultiple, filteredMetricSeries),
    [filteredMultiple, filteredMetricSeries],
  );
  // Ein enger Bewertungszeitraum (1J/3J, teils auch 5J) liefert oft weniger Jahre als
  // FAIR_VALUE.minPeYears, computeFairValueSeries() gab dann `undefined` zurueck - das
  // ersetzte fruehher die GESAMTE Komponente (Chart, Multiples, Zeitraum-Vergleich,
  // Aktienkauf-Rechner) durch einen einzeiligen Hinweistext, reproduzierbar bei jeder
  // Aktie (backlog.md -> Feedback-Runde 2026-07-17, Punkt 9). Fallback auf die volle
  // Historie statt Totalausfall, mit sichtbarem Hinweis statt stillem Umschalten.
  const fairValueFull = useMemo(
    () => computeFairValueSeries(fullMultiple, fullMetricSeries),
    [fullMultiple, fullMetricSeries],
  );
  // Zweiter, niedrigerer Fallback (backlog.md -> Runde 8 Punkt 10): betrifft Aktien, bei
  // denen selbst die VOLLE Historie FAIR_VALUE.minPeYears nicht erreicht - strukturell fast
  // immer Aktien ohne SEC-Registrierung/US-ADR (Yahoo liefert dort oft nur ~4-5 statt
  // SEC-EDGARs 10+ Jahre). Ohne diesen zweiten Boden blieb das komplette Werkzeug (Chart,
  // Multiples, Zeitraum-Vergleich, Aktienkauf-Rechner) fuer diese Aktien dauerhaft durch
  // einen einzeiligen Hinweistext ersetzt - sichtbar als Hinweis ausgewiesen (siehe
  // usedReducedHistory unten), kein stilles Umschalten.
  const fairValueReduced = useMemo(
    () =>
      fairValueFull === undefined
        ? computeFairValueSeries(fullMultiple, fullMetricSeries, FAIR_VALUE.minPeYearsReduced)
        : undefined,
    [fairValueFull, fullMultiple, fullMetricSeries],
  );
  const usedFallbackRange = fairValueForRange === undefined && fairValueFull !== undefined;
  const usedReducedHistory = fairValueFull === undefined && fairValueReduced !== undefined;
  const fairValue = fairValueForRange ?? fairValueFull ?? fairValueReduced;
  const activeMetricSeries = usedFallbackRange || usedReducedHistory ? fullMetricSeries : filteredMetricSeries;

  // Editierbare KGV/KUV-Annahme (backlog.md -> [Punkt 48]): ein gueltiger, positiver
  // Nutzerwert ersetzt den echten historischen Durchschnitt ÜBERALL, wo dieser sonst als
  // Multiplikator dient (Chart-Linie, TTM-Bruecke, Prognose, Prognosekorridor) - "Ø-KGV"
  // in der Multiples-Tabelle unten bleibt bewusst UNVERAENDERT der echte historische Wert
  // (reine Fakten-Anzeige, keine Annahme), nur die FAIR-VALUE-LINIE selbst folgt der
  // Annahme, exakt wie im Aktienfinder-Vorbild beschrieben ("Linienverlauf... aktualisiert
  // sich dynamisch mit").
  const customMultipleOverride = useMemo(() => {
    const parsed = Number(customMultipleDraft.replace(",", "."));
    return customMultipleDraft.trim() !== "" && Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }, [customMultipleDraft]);
  const effectiveAveragePe = customMultipleOverride ?? fairValue?.averagePe;
  const fairValuePoints = useMemo(
    () =>
      customMultipleOverride !== undefined
        ? computeFairValueSeriesForMultiple(activeMetricSeries, customMultipleOverride)
        : (fairValue?.points ?? []),
    [customMultipleOverride, activeMetricSeries, fairValue],
  );

  const lastHistoricalYear = useMemo(() => {
    const validYears = activeMetricSeries.filter((e) => e.value > 0).map((e) => e.year);
    return validYears.length > 0 ? Math.max(...validYears) : undefined;
  }, [activeMetricSeries]);
  const currentYear = new Date().getUTCFullYear();
  // TTM-Bruecke fuers laufende Jahr (Erik 2026-07-14: "2026 muss unbedingt mit dem
  // fairen Wert hinterlegt werden") - schliesst die Luecke zwischen dem letzten
  // testierten Jahresabschluss und der ersten Analysten-Schaetzung, ohne auf eine
  // moeglicherweise fehlende "0y"-Schaetzung angewiesen zu sein. Nur fuer die
  // Gewinn-Kennzahl: fuer Umsatz fehlt ein trailing Umsatz-Wert (siehe Doc-Kommentar oben).
  const currentYearPoint = useMemo(
    () =>
      effectiveAveragePe !== undefined && metric === "eps"
        ? computeCurrentYearFairValue(currentEps, effectiveAveragePe, currentYear, lastHistoricalYear)
        : undefined,
    [effectiveAveragePe, metric, currentEps, currentYear, lastHistoricalYear],
  );
  // Sobald die TTM-Bruecke das laufende Jahr abdeckt, startet die Analysten-Prognose
  // erst danach (sonst gaebe es zwei konkurrierende Punkte fuers selbe Jahr). Ohne
  // TTM-EPS bleibt das alte Verhalten erhalten (Prognose fuellt die Luecke direkt).
  const forecastCutoffYear = currentYearPoint ? currentYear : (lastHistoricalYear ?? currentYear - 1);
  // Letzte bekannte Aktienanzahl als Naeherung fuer kuenftige Jahre (keine eigene
  // Aktienanzahl-Prognose verfuegbar) - nur fuer die Umsatz-je-Aktie-Prognose gebraucht.
  const latestShares = useMemo(() => {
    const sorted = [...sharesOutstanding].filter((s) => s.value > 0).sort((a, b) => a.year - b.year);
    return sorted[sorted.length - 1]?.value;
  }, [sharesOutstanding]);
  const forecastValues = useMemo(
    () =>
      metric === "eps"
        ? perYearEstimates.map((p) => ({ year: p.year, value: p.eps }))
        : perYearEstimates.map((p) => ({
            year: p.year,
            value: p.revenue !== undefined && latestShares ? p.revenue / latestShares : undefined,
          })),
    [metric, perYearEstimates, latestShares],
  );
  const forecast = useMemo(
    () =>
      effectiveAveragePe !== undefined ? computeFairValueForecast(forecastValues, effectiveAveragePe, forecastCutoffYear) : [],
    [effectiveAveragePe, forecastCutoffYear, forecastValues],
  );
  // Prognose-Extrapolation ueber die echten Analystenschaetzjahre hinaus (backlog.md ->
  // [Punkt 51], "Prognosezeitraum auf 3 Jahre verlaengern") - nur fuer die Gewinn-Kennzahl
  // (Yahoos longTermGrowthRate ist eine Gewinnwachstumsrate, keine Umsatzwachstumsrate).
  // FORECAST_TARGET_YEARS peilt insgesamt 3 Prognosejahre an (echte + extrapolierte
  // zusammen), nicht 3 ZUSAETZLICHE ueber die echten hinaus - deckt sich mit Eriks
  // Wortlaut "die Prognose reicht aktuell nur 1 Jahr... auf 3 Jahre ausweiten".
  const lastRealForecastMetricPoint = useMemo(() => {
    const valid = forecastValues.filter(
      (p) => p.year > forecastCutoffYear && p.value !== undefined && p.value > 0,
    );
    if (valid.length === 0) return undefined;
    const last = valid.reduce((max, p) => (p.year > max.year ? p : max));
    return { year: last.year, value: last.value! };
  }, [forecastValues, forecastCutoffYear]);
  const extrapolatedForecast = useMemo(
    () =>
      metric === "eps" && effectiveAveragePe !== undefined
        ? extrapolateFairValueForecast(
            lastRealForecastMetricPoint,
            longTermGrowthRate,
            Math.max(0, FORECAST_TARGET_YEARS - forecast.length),
            effectiveAveragePe,
          )
        : [],
    [metric, effectiveAveragePe, lastRealForecastMetricPoint, longTermGrowthRate, forecast.length],
  );
  // Prognosekorridor optimistisch/pessimistisch (§B.2) - nur fuer die Gewinn-Kennzahl,
  // da Yahoos Umsatz-Bandbreite (low/high) bislang nicht angebunden ist. Bewusst NICHT
  // extrapoliert (anders als die zentrale Prognoselinie oben) - es gibt keine sinnvolle
  // "Wachstumsrate fuer die Unsicherheitsspanne selbst", nur echte Analysten-Low/High-Jahre.
  const forecastBand = useMemo(
    () =>
      effectiveAveragePe !== undefined && metric === "eps"
        ? computeFairValueForecastBand(
            perYearEstimates.map((p) => ({ year: p.year, low: p.epsLow, high: p.epsHigh })),
            effectiveAveragePe,
            forecastCutoffYear,
          )
        : [],
    [effectiveAveragePe, metric, perYearEstimates, forecastCutoffYear],
  );

  // Alternative Fair-Value-Hilfslinien (Nutzer-Feedback 2026-07-18): niedrigster/
  // hoechster valider historischer Multiplikator statt des Durchschnitts - macht den
  // Zwei-Punkte-Klick-Vergleich unten auch fuer eine konservativere/optimistischere
  // Annahme nutzbar, nicht nur fuer Kurs oder die eine Durchschnittslinie. Gleiche
  // Fallback-Logik wie fairValue oben (voller statt gefilterter Zeitraum, falls zu
  // wenige Jahre im gewaehlten Bewertungszeitraum liegen), gleicher forecastCutoffYear
  // wie die Durchschnittslinie - nur der Multiplikator selbst unterscheidet sich.
  const activeMultipleSeries = usedFallbackRange || usedReducedHistory ? fullMultiple : filteredMultiple;
  const altMultiples = useMemo(
    () =>
      multipleRange(activeMultipleSeries, usedReducedHistory ? FAIR_VALUE.minPeYearsReduced : FAIR_VALUE.minPeYears),
    [activeMultipleSeries, usedReducedHistory],
  );
  const [showAlternatives, setShowAlternatives] = useState(true);

  const conservativeSeries = useMemo(
    () => (altMultiples ? computeFairValueSeriesForMultiple(activeMetricSeries, altMultiples.min) : []),
    [altMultiples, activeMetricSeries],
  );
  const optimisticSeries = useMemo(
    () => (altMultiples ? computeFairValueSeriesForMultiple(activeMetricSeries, altMultiples.max) : []),
    [altMultiples, activeMetricSeries],
  );
  const conservativeCurrentYearPoint = useMemo(
    () =>
      altMultiples && metric === "eps"
        ? computeCurrentYearFairValue(currentEps, altMultiples.min, currentYear, lastHistoricalYear)
        : undefined,
    [altMultiples, metric, currentEps, currentYear, lastHistoricalYear],
  );
  const optimisticCurrentYearPoint = useMemo(
    () =>
      altMultiples && metric === "eps"
        ? computeCurrentYearFairValue(currentEps, altMultiples.max, currentYear, lastHistoricalYear)
        : undefined,
    [altMultiples, metric, currentEps, currentYear, lastHistoricalYear],
  );
  const conservativeForecast = useMemo(
    () => (altMultiples ? computeFairValueForecast(forecastValues, altMultiples.min, forecastCutoffYear) : []),
    [altMultiples, forecastValues, forecastCutoffYear],
  );
  const optimisticForecast = useMemo(
    () => (altMultiples ? computeFairValueForecast(forecastValues, altMultiples.max, forecastCutoffYear) : []),
    [altMultiples, forecastValues, forecastCutoffYear],
  );

  // Multiples-Tabelle zeigt KGV UND KUV immer beide (sofern berechenbar), unabhaengig
  // vom `metric`-Umschalter oben, der nur bestimmt, welche der beiden die Fair-Value-
  // Linie im Chart antreibt (backlog.md -> §B "noch nicht KUV" - jetzt ergaenzt).
  const avgPe = useMemo(() => averageOf(filteredPe.filter((v) => v.value > 0)), [filteredPe]);
  const avgPriceToSales = useMemo(
    () => averageOf(filteredPriceToSales.filter((v) => v.value > 0)),
    [filteredPriceToSales],
  );

  // Fair-Value-Linienfarbe je Bewertungskennzahl (backlog.md -> Feedback-Runde
  // 2026-07-17, Punkt 8): GOLD blieb bisher fest, unabhaengig von `metric` - jetzt
  // dasselbe bereits im Projekt etablierte Dunkelgruen (POSITIVE) fuer "eps", GOLD
  // bleibt fuer "revenue". Treibt Linie, Legenden-Farbpunkt UND Prognoseband-Fuellung
  // gemeinsam, sonst wirkt eine nur halbe Umsetzung inkonsistent.
  const fairValueColor = metric === "eps" ? POSITIVE : GOLD;

  const priceDigits = useMemo(
    () => pricePrecisionDigits(priceHistory[priceHistory.length - 1]?.close),
    [priceHistory],
  );
  const priceFormatter = useMemo(
    () =>
      new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency,
        minimumFractionDigits: priceDigits,
        maximumFractionDigits: priceDigits,
      }),
    [currency, priceDigits],
  );

  const filteredPrice = useMemo(() => filterDateRange(priceHistory, range), [priceHistory, range]);
  const renderedPrice = useMemo(
    () => withTimeField(downsample(filteredPrice, 1000, (p) => p.close)),
    [filteredPrice],
  );

  const { chartData, lastPriceTime, lastRowTime } = useMemo(() => {
    const rows = new Map<number, ChartRow>();
    for (const p of renderedPrice) {
      rows.set(p.time, { time: p.time, close: p.close });
    }
    const lastPrice = renderedPrice[renderedPrice.length - 1]?.time;

    function upsert(time: number, patch: Partial<ChartRow>) {
      rows.set(time, { ...(rows.get(time) ?? { time }), ...patch });
    }

    for (const point of fairValuePoints) {
      const time = Date.UTC(point.year, 11, 31);
      upsert(time, { fairValue: point.fairValue });
    }
    for (const point of conservativeSeries) {
      upsert(Date.UTC(point.year, 11, 31), { fairValueConservative: point.fairValue });
    }
    for (const point of optimisticSeries) {
      upsert(Date.UTC(point.year, 11, 31), { fairValueOptimistic: point.fairValue });
    }

    // TTM-Bruecke sitzt exakt am letzten echten Kurspunkt ("heute"), nicht an einem
    // erst noch kommenden Jahresende - so bleiben alle drei Fair-Value-artigen Linien
    // luecken- und sprungfrei bis zum aktuellen Handelstag.
    if (lastPrice !== undefined) {
      if (currentYearPoint) upsert(lastPrice, { fairValue: currentYearPoint.fairValue });
      if (conservativeCurrentYearPoint) {
        upsert(lastPrice, { fairValueConservative: conservativeCurrentYearPoint.fairValue });
      }
      if (optimisticCurrentYearPoint) {
        upsert(lastPrice, { fairValueOptimistic: optimisticCurrentYearPoint.fairValue });
      }
    }

    for (const point of forecast) {
      const time = Date.UTC(point.year, 11, 31);
      upsert(time, { fairValue: point.fairValue });
    }

    // Extrapolierte Prognose (backlog.md -> [Punkt 51]) - Anker exakt am letzten ECHTEN
    // Prognosepunkt (oder der TTM-Bruecke, falls es gar kein echtes Schaetzjahr gibt),
    // damit die gestrichelte Extrapolation dort nahtlos weiterlaeuft, wo die echte
    // Prognose endet, statt eines optischen Sprungs.
    if (extrapolatedForecast.length > 0) {
      const lastReal = forecast[forecast.length - 1];
      if (lastReal) {
        upsert(Date.UTC(lastReal.year, 11, 31), { fairValueExtrapolated: lastReal.fairValue });
      } else if (currentYearPoint && lastPrice !== undefined) {
        upsert(lastPrice, { fairValueExtrapolated: currentYearPoint.fairValue });
      }
      for (const point of extrapolatedForecast) {
        upsert(Date.UTC(point.year, 11, 31), { fairValueExtrapolated: point.fairValue });
      }
    }

    for (const point of conservativeForecast) {
      upsert(Date.UTC(point.year, 11, 31), { fairValueConservative: point.fairValue });
    }
    for (const point of optimisticForecast) {
      upsert(Date.UTC(point.year, 11, 31), { fairValueOptimistic: point.fairValue });
    }

    if (forecastBand.length > 0) {
      // Nullbreite-Ankerpunkt genau dort, wo die Prognose beginnt (TTM-Bruecke "heute"
      // oder das letzte historische Jahr) - eine Flaeche braucht mindestens zwei Punkte,
      // sonst verschwindet der Korridor bei nur einem echten Schaetzjahr (z.B. wenn "0y"
      // schon von der TTM-Bruecke abgedeckt ist und nur "+1y" fuer den Korridor uebrig
      // bleibt). Ergibt den erwuenschten, sich trichterfoermig oeffnenden Korridor statt
      // eines rechteckigen Bands ab dem ersten Schaetzjahr.
      const anchorTime = currentYearPoint && lastPrice !== undefined ? lastPrice : Date.UTC(forecastCutoffYear, 11, 31);
      const anchorFairValue = currentYearPoint?.fairValue ?? fairValuePoints.find((p) => p.year === forecastCutoffYear)?.fairValue;
      if (anchorFairValue !== undefined) {
        upsert(anchorTime, { forecastBase: anchorFairValue, forecastSpread: 0 });
      }
    }

    for (const point of forecastBand) {
      const time = Date.UTC(point.year, 11, 31);
      upsert(time, { forecastBase: point.low, forecastSpread: point.high - point.low });
    }

    const sorted = [...rows.values()].sort((a, b) => a.time - b.time);

    // Fair Value nur an Jahres-/Prognose-Stichtagen zu setzen liesse den Tooltip an fast
    // jedem Tag dazwischen leer bleiben (Erik: "sieht man nur den Fair Value, wenn man im
    // Prognosezeitraum ist") - dabei zeichnet Recharts' `connectNulls` die Linien durch
    // genau diese Luecken optisch bereits linear interpoliert. Hier wird exakt dieselbe
    // lineare Interpolation auf JEDEN Tageswert angewandt (Zwei-Zeiger-Durchlauf ueber die
    // bereits sortierten Stichtage), damit Tooltip UND Klick-Aufloesung beim Hovern/Klicken
    // ueberall denselben Wert liefern, den die jeweilige Linie an dieser Stelle sichtbar
    // hat - jetzt fuer alle drei Fair-Value-artigen Linien gleichermassen (FAIR_VALUE_KEYS).
    for (const key of FAIR_VALUE_KEYS) {
      const anchors = sorted.filter((r) => r[key] !== undefined).map((r) => ({ time: r.time, value: r[key]! }));
      if (anchors.length === 0) continue;
      let i = 0;
      for (const row of sorted) {
        if (row[key] !== undefined) continue;
        while (i + 1 < anchors.length && anchors[i + 1].time <= row.time) i++;
        const prev = anchors[i];
        const next = anchors[i + 1];
        // Invariante nach der while-Schleife: next (falls vorhanden) hat immer
        // next.time > row.time - vor dem ersten bzw. nach dem letzten Anker wird dessen
        // Wert flach fortgeschrieben, dazwischen linear interpoliert.
        if (row.time <= prev.time || !next) {
          row[key] = prev.value;
        } else {
          const t = (row.time - prev.time) / (next.time - prev.time);
          row[key] = prev.value + t * (next.value - prev.value);
        }
      }
    }

    return {
      chartData: sorted,
      lastPriceTime: lastPrice,
      lastRowTime: sorted[sorted.length - 1]?.time,
    };
  }, [
    renderedPrice,
    fairValuePoints,
    forecast,
    extrapolatedForecast,
    forecastBand,
    forecastCutoffYear,
    currentYearPoint,
    conservativeSeries,
    optimisticSeries,
    conservativeCurrentYearPoint,
    optimisticCurrentYearPoint,
    conservativeForecast,
    optimisticForecast,
  ]);

  // Feedback-Runde 19 Punkt 16: die drei Analysten-Kurszielinien sollen nur die
  // Prognosezone ueberspannen (dieselbe Zone wie die "Prognosezone"-ReferenceArea
  // unten), nicht wie bisher den kompletten, ggf. mehrjaehrigen Chart - ein
  // Konsens-Kursziel ist eine vorwaertsgerichtete Erwartung, keine rueckwirkend
  // gueltige Aussage ueber die komplette historische Kursspanne. Weiter nach oben
  // verschoben (vorher direkt vor dem JSX-Return) - wird jetzt zusaetzlich fuer
  // chartDataWithAnalystTargets unten gebraucht, nicht mehr nur fuer die JSX-Elemente.
  const forecastZone = useMemo(
    () =>
      lastPriceTime !== undefined && lastRowTime !== undefined && lastRowTime > lastPriceTime
        ? { x1: lastPriceTime, x2: lastRowTime }
        : undefined,
    [lastPriceTime, lastRowTime],
  );

  // Feedback-Runde 19 B/16 (Blackstone-Nachuntersuchung, siehe backlog.md): macht die
  // drei Analysten-Kurszielinien klickbar, inkl. "dazwischen" (naechstgelegene Serie
  // gewinnt, gleiches Prinzip wie bei den uebrigen 4 Serien) - vorher reine
  // ReferenceLine-Elemente ohne dataKey, fuer Recharts' activeDataKey-Ermittlung also
  // unsichtbar. Eigene, kleine Ableitung statt Teil des grossen chartData-Memos oben:
  // die Werte sind konstant (ein einzelnes Konsensziel, keine Zeitreihe), eine
  // Wiederverwendung der bestehenden FAIR_VALUE_KEYS-Interpolationsschleife wuerde den
  // Wert dagegen faelschlich auch VOR/NACH der Prognosezone flach fortschreiben (siehe
  // deren eigener Kommentar). Ohne forecastZone (Randfall: Kurszielpunkt vorhanden,
  // aber keine Prognosejahre) faellt das auf eine volle Chart-Breite zurueck, exakt wie
  // zuvor beim ReferenceLine-Fallback `{ y: priceTargetMean }`.
  const chartDataWithAnalystTargets = useMemo(() => {
    if (priceTargetMean === undefined && priceTargetHigh === undefined && priceTargetLow === undefined) {
      return chartData;
    }
    return chartData.map((row) => {
      const inZone = forecastZone ? row.time >= forecastZone.x1 && row.time <= forecastZone.x2 : true;
      if (!inZone) return row;
      return { ...row, analystTargetMean: priceTargetMean, analystTargetHigh: priceTargetHigh, analystTargetLow: priceTargetLow };
    });
  }, [chartData, forecastZone, priceTargetMean, priceTargetHigh, priceTargetLow]);

  const isNarrow = useIsNarrowViewport();
  // Ticks aus chartData (nicht nur renderedPrice) berechnen - chartData reicht ueber die
  // Prognosezone hinaus bis zum letzten Fair-Value-/Prognosepunkt, renderedPrice endet
  // beim letzten echten Kurstag. Mit nur renderedPrice fehlte der Prognosezone rechts ein
  // eigener Jahres-Tick (Achsen-Domain via dataMin/dataMax deckt sie ab, Ticks vorher nicht).
  const axisTicks = useMemo(
    () =>
      computePriceAxisTicks(
        chartData.map((row) => ({ date: new Date(row.time).toISOString() })),
        range,
        isNarrow ? 5 : 8,
      ).map((d) => Date.parse(d)),
    [chartData, range, isNarrow],
  );

  const forecastYearsCount = forecast.length;
  const forecastZoneDescription = (() => {
    const parts: string[] = [];
    if (currentYearPoint) parts.push("das laufende Jahr auf TTM-Basis");
    if (forecastYearsCount > 0) {
      parts.push(`${forecastYearsCount} Jahr${forecastYearsCount === 1 ? "" : "e"} echter Analystenschätzung`);
    }
    if (parts.length === 0) return "zukünftige Analystenschätzungen, sofern verfügbar";
    return parts.join(" sowie ");
  })();
  const lastForecastPoint = forecast[forecast.length - 1];
  const horizonYears = lastForecastPoint ? lastForecastPoint.year - new Date().getFullYear() : undefined;

  // Freie Zwei-Punkt-Simulation (backlog.md -> §B.4 / Master-Prompt 5, Erik 2026-07-14):
  // Lookup-Map fuer O(1)-Klick-Aufloesung statt eines linearen Scans ueber bis zu ~1000
  // Punkte je Klick - wichtig fuer "flüssig ohne Latenz", da chartData bei "Max" gross
  // werden kann. Zustandsupdate passiert NUR bei tatsaechlichen Klicks (kein
  // mousemove-getriebenes Re-Rendering), das haelt die Interaktion leichtgewichtig.
  const chartDataByTime = useMemo(
    () => new Map(chartDataWithAnalystTargets.map((row) => [row.time, row])),
    [chartDataWithAnalystTargets],
  );
  // `source` ist reine UI-Zusatzinfo (Feedback-Runde 19 B/16, Blackstone-Meldung
  // "Pers. Div.-Rendite p. a. zu niedrig") - eine der beiden moeglichen Root-Causes war,
  // dass ein Klick naeher an der Fair-Value- als der Kurs-Linie lag und dadurch den
  // modellierten statt des echten historischen Kurses als Nenner verwendet hat, ohne
  // dass das irgendwo sichtbar war. `SelectedPoint` erweitert `IntervalEndpoint`
  // (structurally kompatibel, computeIntervalPerformance() bekommt weiterhin nur
  // {time,value}) statt die reine Rechenfunktion um ein UI-Feld zu erweitern.
  type SelectedPoint = IntervalEndpoint & { source: keyof typeof SOURCE_LABELS };
  const [selection, setSelection] = useState<{ a: SelectedPoint | null; b: SelectedPoint | null }>({
    a: null,
    b: null,
  });

  const addSelectionPoint = useCallback((point: SelectedPoint) => {
    setSelection((prev) => {
      if (!prev.a || prev.b) return { a: point, b: null };
      if (prev.a.time === point.time) return prev;
      return { a: prev.a, b: point };
    });
  }, []);

  const handleChartClick = useCallback(
    (state: MouseHandlerDataParam) => {
      const time = typeof state.activeLabel === "number" ? state.activeLabel : Number(state.activeLabel);
      if (!Number.isFinite(time)) return;
      const row = chartDataByTime.get(time);
      if (!row) return;
      // Erik: "man will ja wissen, welche Performance man macht, wenn man den Fair Value
      // anklickt" - Recharts meldet in `activeDataKey`, welche Serie dem Klick
      // pixelmaessig am naechsten war (nicht nur den X-Zeitpunkt). BUGFIX (2026-07-18):
      // vorher gab es dadurch effektiv nur "Kurs" oder die eine Durchschnitts-Fair-Value-
      // Linie als moegliches Klickziel - egal wo vertikal geklickt wurde, kam nie ein
      // dritter Wert heraus ("nicht nur auf der gruenen Linie klickbar" aus dem
      // Feedback). Erweiterbare Kandidatenliste statt einer tief verschachtelten Ternary
      // (Feedback-Runde 19 B/16: drei weitere Analysten-Kurszielinien dazugekommen, siehe
      // chartDataWithAnalystTargets oben) - "naechstgelegene Serie gewinnt" gilt jetzt
      // gleichberechtigt fuer alle 7 Serien, macht auch Klicks ZWISCHEN zwei Linien
      // automatisch moeglich (kein Sonderfall noetig).
      const key = state.activeDataKey;
      const candidates: { source: keyof typeof SOURCE_LABELS; enabled: boolean; value: number | undefined }[] = [
        { source: "fairValueConservative", enabled: showAlternatives && key === "fairValueConservative", value: row.fairValueConservative },
        { source: "fairValueOptimistic", enabled: showAlternatives && key === "fairValueOptimistic", value: row.fairValueOptimistic },
        { source: "fairValueExtrapolated", enabled: key === "fairValueExtrapolated", value: row.fairValueExtrapolated },
        { source: "fairValue", enabled: key === "fairValue", value: row.fairValue },
        { source: "analystTargetMean", enabled: key === "analystTargetMean", value: row.analystTargetMean },
        { source: "analystTargetHigh", enabled: key === "analystTargetHigh", value: row.analystTargetHigh },
        { source: "analystTargetLow", enabled: key === "analystTargetLow", value: row.analystTargetLow },
      ];
      const matched = candidates.find((c) => c.enabled && c.value !== undefined);
      const value = matched?.value ?? row.close ?? row.fairValue;
      if (value === undefined) return;
      const source: keyof typeof SOURCE_LABELS = matched?.source ?? (row.close !== undefined ? "close" : "fairValue");
      addSelectionPoint({ time, value, source });
    },
    [chartDataByTime, addSelectionPoint, showAlternatives],
  );

  // Hover-Entrümpelung (Feedback-Runde 16 Punkt 6, 2026-07-22): bislang zeigten Tooltip
  // UND Active-Dots aller vier Serien (Kurs + 3 Fair-Value-artige Linien) gleichzeitig,
  // sobald man irgendwo im Chart hoverte. `activeDataKey` ist exakt derselbe von Recharts
  // berechnete "naechstgelegene Serie"-Wert, den handleChartClick oben schon fuer den
  // Klick-Fall nutzt - hier gleiche Logik fuers blosse Hovern, damit nur noch der
  // tatsaechlich naechstgelegene Punkt hervorgehoben wird.
  const [hoveredKey, setHoveredKey] = useState<string | undefined>(undefined);
  const handleChartHover = useCallback((state: MouseHandlerDataParam) => {
    setHoveredKey(typeof state.activeDataKey === "string" ? state.activeDataKey : undefined);
  }, []);
  const handleChartMouseLeave = useCallback(() => setHoveredKey(undefined), []);

  const resetSelection = useCallback(() => setSelection({ a: null, b: null }), []);
  const intervalResult = useMemo(
    () => (selection.a && selection.b ? computeIntervalPerformance(selection.a, selection.b, dividendPerShare) : undefined),
    [selection, dividendPerShare],
  );
  // Chronologisch sortierte Quell-Label (Kurs/Fair Value/Konservativ/Optimistisch) fuer
  // Start-/Endpunkt - computeIntervalPerformance() sortiert intern selbst nach Zeit
  // (Klickreihenfolge ist egal), diese Sortierung hier folgt derselben Logik nur fuer
  // die Anzeige, siehe SOURCE_LABELS-Kommentar oben.
  const intervalSources = useMemo(() => {
    if (!selection.a || !selection.b) return undefined;
    return selection.a.time < selection.b.time
      ? { start: selection.a.source, end: selection.b.source }
      : { start: selection.b.source, end: selection.a.source };
  }, [selection]);

  const amount = Number(amountDraft.replace(",", "."));
  const latestDividend = latestValue(dividendPerShare) ?? 0;
  const simulation = useMemo(() => {
    if (
      !lastForecastPoint ||
      currentPrice === undefined ||
      !Number.isFinite(amount) ||
      amount <= 0 ||
      horizonYears === undefined ||
      horizonYears <= 0
    ) {
      return undefined;
    }
    const startDate = new Date();
    const endDate = new Date(Date.UTC(lastForecastPoint.year, 11, 31));
    return simulatePurchase({
      amount,
      currentPrice,
      targetPrice: lastForecastPoint.fairValue,
      expectedDividendPerShare: latestDividend * horizonYears,
      startDate,
      endDate,
    });
  }, [lastForecastPoint, currentPrice, amount, horizonYears, latestDividend]);

  if (priceHistory.length === 0) {
    return <p className="text-sm text-muted-foreground">Kursverlauf aktuell nicht verfügbar.</p>;
  }

  if (!fairValue) {
    return (
      <p className="text-sm text-muted-foreground">
        Für einen Fairer-Wert-Simulator werden mindestens {FAIR_VALUE.minPeYearsReduced} Jahre mit
        einem positiven {metric === "eps" ? "KGV" : "KUV"} benötigt – dafür liegen für diese Aktie
        insgesamt nicht genügend Daten vor.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Feedback-Runde 19 Punkt 17 (2026-07-27, Vorbild Aktienfinder): Chart-Spalte von
          2/3 (66,7 %) auf 3/4 (75 %) vergroessert, Rechen-Panel entsprechend von 1/3 auf
          1/4 kompakter - Panel-Inhalte (Multiples-Tabelle, Zeitraum-Vergleich, Aktienkauf-
          Rechner) nutzen durchgehend justify-between mit umbruchfaehigen Spans statt
          fixer Breiten, degradieren also graceful (Zeilenumbruch) statt abzuschneiden,
          falls der laengste Zeilenlabel ("Pers. Div.-Rendite p. a.") bei der schmaleren
          Breite eng wird. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        <div className="lg:col-span-3">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span style={{ color: NAVY }}>■</span> Kurs
              </span>
              <label className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={showFairValue}
                  onChange={(e) => setShowFairValue(e.target.checked)}
                />
                <span style={{ color: fairValueColor }}>■</span> <GlossaryTerm termKey="fairValue" /> (Ø-
                {metric === "eps" ? "KGV" : "KUV"})
              </label>
              {altMultiples && (
                <label className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={showAlternatives}
                    onChange={(e) => setShowAlternatives(e.target.checked)}
                  />
                  <span style={{ color: fairValueColor, opacity: 0.5 }}>■</span> Alternative Annahmen (Min/
                  Max-{metric === "eps" ? "KGV" : "KUV"})
                </label>
              )}
            </div>
            {/* Bug-Fix (Erik 2026-08-20, "wenn man 3 Jahre anwählt, wird der Chart falsch
                dargestellt"): bei usedFallbackRange zeigt der Chart tatsaechlich die volle
                Historie (fullMetricSeries/fullMultiple, siehe activeMetricSeries oben),
                waehrend der Selector bisher trotzdem weiterhin "range" (z.B. "3J") als aktiv
                markierte - ein direkter Widerspruch zum tatsaechlich Angezeigten. Zeigt jetzt
                ehrlich "Max" als aktiv, sobald der Fallback greift; der Hinweistext darunter
                erklaert weiterhin, dass das eine automatische Ersetzung der eigentlichen
                Auswahl ist, kein Klick auf "Max" noetig. */}
            <RangeSelector value={usedFallbackRange ? "Max" : range} onChange={onRangeChange} />
          </div>

          <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-2">
              Bewertungskennzahl
              <MetricToggle value={metric} onChange={setMetric} />
            </span>
            {/* Editierbare KGV/KUV-Annahme (backlog.md -> [Punkt 48], Vorbild Aktienfinder):
                leeres Feld = echter historischer Durchschnitt (Placeholder zeigt dessen
                Wert an), jede gueltige positive Eingabe ersetzt ihn ueberall dort, wo er
                sonst als Multiplikator dient - siehe customMultipleOverride oben. */}
            <label className="flex items-center gap-1.5" title="Eigene Ø-KGV/KUV-Annahme statt des echten historischen Durchschnitts - Linienverlauf und Prognose passen sich sofort an.">
              Ø-{metric === "eps" ? "KGV" : "KUV"}-Annahme
              <Input
                type="number"
                min={0}
                step="any"
                value={customMultipleDraft}
                onChange={(e) => setCustomMultipleDraft(e.target.value)}
                placeholder={formatRatio(fairValue.averagePe)}
                className="h-7 w-16 bg-card text-right"
              />
              {customMultipleOverride !== undefined && (
                <button
                  type="button"
                  onClick={() => setCustomMultipleDraft("")}
                  className="text-primary hover:text-primary/80"
                  aria-label="Eigene KGV/KUV-Annahme zuruecksetzen"
                  title="Zuruecksetzen auf den echten historischen Durchschnitt"
                >
                  <RotateCcw className="h-3 w-3" />
                </button>
              )}
            </label>
            <label
              className="flex items-center gap-1.5"
              title="Blendet den Prognosekorridor aus (auf einer logarithmischen Achse nicht sauber darstellbar)"
            >
              <input type="checkbox" checked={logScale} onChange={(e) => setLogScale(e.target.checked)} />
              Log-Skala
            </label>
          </div>

          {usedFallbackRange && (
            <p className="mb-3 text-xs text-muted-foreground">
              Zeitraum „{valuationRange}“ liefert zu wenige Jahre mit gültigem{" "}
              {metric === "eps" ? "KGV" : "KUV"} (mind. {FAIR_VALUE.minPeYears} nötig) – zeige stattdessen
              die volle verfügbare Historie ({fairValue.yearsUsed} Jahre) für den KGV/KUV-Schnitt.
            </p>
          )}
          {usedReducedHistory && (
            <p className="mb-3 text-xs text-muted-foreground">
              Für diese Aktie liegen insgesamt nur {fairValue.yearsUsed} Jahre mit gültigem{" "}
              {metric === "eps" ? "KGV" : "KUV"} vor (regulär empfohlen: mind. {FAIR_VALUE.minPeYears}{" "}
              Jahre, meist bei Aktien ohne SEC-Registrierung/US-ADR der Fall) – der Durchschnitts-
              {metric === "eps" ? "KGV" : "KUV"} basiert daher auf einer kleineren, weniger
              belastbaren Datengrundlage.
            </p>
          )}

          {!selection.a && (
            <p className="mb-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
              💡 Klicke zwei Punkte im Chart an, um Performance & Dividendenrendite für
              diesen Zeitraum zu sehen
            </p>
          )}

          {/* BUGFIX (2026-07-18): Recharts' eingebauter Accessibility-Layer (accessibilityLayer,
              Default true) macht das Chart-SVG per tabIndex fokussierbar - nach einem Klick
              blieb dadurch ein Browser-Default-Fokusring (teils gelblich, je nach Betriebssystem/
              Theme) sichtbar, der sich beim Weiterklicken ueber den Chart-Bereich bewegte
              ("gelber Balken", Feedback-Runde 4 Punkt 4 + erneut 2026-07-18 gemeldet). Die reine
              CSS-outline-none-Regel unten reichte allein nicht, da der Ring am fokussierten
              SVG-Root haengt, nicht an dessen Kind-Elementen - `accessibilityLayer={false}`
              entfernt tabIndex/role direkt an der Quelle (RootSurface.js), die eigenen aktiven
              Punkte/Linien geben bereits klares visuelles Feedback, die eigene Klick-Interaktion
              ist ohnehin nicht tastatur-/screenreader-gefuehrt. Ergaenzung (2026-07-21,
              Runde 8 Punkt 13): Erik meldete den gelben Rand erneut, auch nach diesem Fix
              - zusaetzlich focus-visible:outline-none ergaenzt (deckt Browser ab, deren
              Default-Fokusindikator ueber die :focus-visible- statt der reinen :focus-
              Pseudoklasse greift). **Root-Cause-Fund (2026-07-22, Feedback-Runde 15 Punkt
              1c)**: Erik meldete das Symptom seitenweit erneut - dieser lokale Fix war nie
              das Problem, sondern lief nur in DIESEM und in multi-kpi-line-chart.tsx, waehrend
              der Grossteil der uebrigen Charts (historical-line-chart.tsx,
              price-history-chart.tsx, technical-chart.tsx) ihn nie erhalten hatte. Jetzt
              zusaetzlich global abgesichert ueber
              `.recharts-surface:focus:not(:focus-visible)` in app/globals.css, greift
              automatisch fuer JEDEN Chart im Projekt statt sich auf lokale [&_svg]-Regeln je
              Komponente zu verlassen. */}
          <div className="h-[28rem] w-full cursor-crosshair [&_svg]:outline-none [&_svg]:focus:outline-none [&_svg]:focus-visible:outline-none">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartDataWithAnalystTargets}
                margin={{ top: 8, right: 24, left: 0, bottom: 0 }}
                onClick={handleChartClick}
                onMouseMove={handleChartHover}
                onMouseLeave={handleChartMouseLeave}
                accessibilityLayer={false}
              >
                <defs>
                  <linearGradient id="fvSimPriceFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={NAVY} stopOpacity={0.22} />
                    <stop offset="100%" stopColor={NAVY} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={NAVY} strokeOpacity={0.12} strokeDasharray="2 4" />
                <XAxis
                  dataKey="time"
                  type="number"
                  domain={["dataMin", "dataMax"]}
                  scale="time"
                  tickFormatter={(v: number) => formatPriceAxisTick(v, range)}
                  ticks={axisTicks}
                  interval={0}
                  tick={{ fontSize: 12, fill: "#6B7280" }}
                  axisLine={{ stroke: "#ECEAE3" }}
                  tickLine={false}
                />
                <YAxis
                  domain={["auto", "auto"]}
                  scale={logScale ? "log" : "linear"}
                  allowDataOverflow={false}
                  tick={{ fontSize: 12, fill: "#6B7280" }}
                  tickFormatter={(v: number) => priceFormatter.format(v)}
                  width={70}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  // Eigener `content`-Renderer statt formatter/contentStyle (Feedback-Runde 16
                  // Punkt 6, 2026-07-22): zeigte vorher bis zu 4 gleichzeitig sichtbare Linien
                  // (Kurs, Fair Value, Min-/Max-Hilfslinie) auf einmal in der Box, was laut Erik
                  // unuebersichtlich wirkt - ActivePointTooltip reduziert das auf genau die
                  // Serie, die `hoveredKey` (siehe handleChartHover oben) gerade als
                  // naechstgelegen markiert.
                  content={(props) => (
                    <ActivePointTooltip
                      active={props.active}
                      payload={props.payload}
                      label={props.label}
                      activeKey={hoveredKey}
                      metric={metric}
                      priceFormatter={priceFormatter}
                    />
                  )}
                  cursor={{ stroke: NAVY, strokeOpacity: 0.25, strokeWidth: 1, strokeDasharray: "3 3" }}
                />
                {forecastZone && (
                  <ReferenceArea
                    x1={forecastZone.x1}
                    x2={forecastZone.x2}
                    fill={NAVY}
                    fillOpacity={0.05}
                    label={{
                      value: "Prognosezone",
                      position: "insideTopLeft",
                      fill: "#6B7280",
                      fontSize: 10,
                    }}
                  />
                )}
                {priceTargetMean !== undefined && (
                  <ReferenceLine
                    {...(forecastZone
                      ? { segment: [{ x: forecastZone.x1, y: priceTargetMean }, { x: forecastZone.x2, y: priceTargetMean }] }
                      : { y: priceTargetMean })}
                    stroke="#6B7280"
                    strokeDasharray="4 4"
                    label={{
                      value: `Analysten-Kursziel (Konsens): ${priceFormatter.format(priceTargetMean)}`,
                      position: "insideTopLeft",
                      fill: "#6B7280",
                      fontSize: 10,
                    }}
                  />
                )}
                {priceTargetHigh !== undefined && (
                  <ReferenceLine
                    {...(forecastZone
                      ? { segment: [{ x: forecastZone.x1, y: priceTargetHigh }, { x: forecastZone.x2, y: priceTargetHigh }] }
                      : { y: priceTargetHigh })}
                    stroke="#6B7280"
                    strokeOpacity={0.5}
                    strokeDasharray="2 3"
                    label={{
                      value: `Analysten-Kursziel (Obergrenze): ${priceFormatter.format(priceTargetHigh)}`,
                      position: "insideTopLeft",
                      fill: "#6B7280",
                      fillOpacity: 0.7,
                      fontSize: 10,
                    }}
                  />
                )}
                {priceTargetLow !== undefined && (
                  <ReferenceLine
                    {...(forecastZone
                      ? { segment: [{ x: forecastZone.x1, y: priceTargetLow }, { x: forecastZone.x2, y: priceTargetLow }] }
                      : { y: priceTargetLow })}
                    stroke="#6B7280"
                    strokeOpacity={0.5}
                    strokeDasharray="2 3"
                    label={{
                      value: `Analysten-Kursziel (Untergrenze): ${priceFormatter.format(priceTargetLow)}`,
                      position: "insideBottomLeft",
                      fill: "#6B7280",
                      fillOpacity: 0.7,
                      fontSize: 10,
                    }}
                  />
                )}
                {/* Unsichtbare Hit-Test-Linien fuer die drei Analysten-Kurszielinien
                    (Feedback-Runde 19 B/16) - die sichtbare Darstellung bleibt komplett den
                    drei ReferenceLine-Elementen oben ueberlassen (unveraendert, inkl. Label-
                    Text), diese echten dataKey-Serien mit transparentem Strich sorgen nur
                    dafuer, dass Recharts' activeDataKey-Ermittlung (Klick UND Hover) sie
                    jetzt als gleichberechtigte Kandidaten kennt statt sie zu ignorieren. Ein
                    kleiner grauer Punkt beim Hover gibt trotzdem sichtbares Feedback, welche
                    der drei Linien gerade "aktiv" ist. */}
                {priceTargetMean !== undefined && (
                  <Line
                    type="linear"
                    dataKey="analystTargetMean"
                    stroke="transparent"
                    dot={false}
                    activeDot={hoveredKey === "analystTargetMean" ? { r: 4, fill: "#6B7280", stroke: "#FAF9F5", strokeWidth: 2 } : false}
                    legendType="none"
                    isAnimationActive={false}
                    connectNulls
                  />
                )}
                {priceTargetHigh !== undefined && (
                  <Line
                    type="linear"
                    dataKey="analystTargetHigh"
                    stroke="transparent"
                    dot={false}
                    activeDot={hoveredKey === "analystTargetHigh" ? { r: 4, fill: "#6B7280", stroke: "#FAF9F5", strokeWidth: 2 } : false}
                    legendType="none"
                    isAnimationActive={false}
                    connectNulls
                  />
                )}
                {priceTargetLow !== undefined && (
                  <Line
                    type="linear"
                    dataKey="analystTargetLow"
                    stroke="transparent"
                    dot={false}
                    activeDot={hoveredKey === "analystTargetLow" ? { r: 4, fill: "#6B7280", stroke: "#FAF9F5", strokeWidth: 2 } : false}
                    legendType="none"
                    isAnimationActive={false}
                    connectNulls
                  />
                )}
                {showFairValue && forecastBand.length > 0 && !logScale && (
                  <>
                    <Area
                      dataKey="forecastBase"
                      stackId="forecastBand"
                      stroke="none"
                      fill="transparent"
                      isAnimationActive={false}
                      connectNulls
                    />
                    <Area
                      dataKey="forecastSpread"
                      stackId="forecastBand"
                      stroke="none"
                      fill={fairValueColor}
                      fillOpacity={0.15}
                      isAnimationActive={false}
                      connectNulls
                    />
                  </>
                )}
                <Area
                  type="linear"
                  dataKey="close"
                  stroke={NAVY}
                  strokeWidth={1.75}
                  fill="url(#fvSimPriceFill)"
                  dot={false}
                  activeDot={hoveredKey === "close" ? { r: 4, fill: NAVY, stroke: "#FAF9F5", strokeWidth: 2 } : false}
                  isAnimationActive={false}
                  connectNulls
                />
                {showFairValue && (
                  <Line
                    type="linear"
                    dataKey="fairValue"
                    stroke={fairValueColor}
                    strokeWidth={3}
                    strokeDasharray="6 4"
                    dot={false}
                    activeDot={
                      hoveredKey === "fairValue" ? { r: 5, fill: fairValueColor, stroke: "#FAF9F5", strokeWidth: 2 } : false
                    }
                    isAnimationActive={false}
                    connectNulls
                  />
                )}
                {/* Wachstums-Extrapolation ueber die echten Analystenschaetzjahre hinaus
                    (backlog.md -> [Punkt 51]) - fein gestrichelt statt der 6-4-Dashierung
                    der Haupt-Fair-Value-Linie (gleiche Konvention wie die "(Extrapolation)"-
                    Kennzeichnung im Nachbar-Chart "Bewertungskennzahlen im Verlauf"), damit
                    auf den ersten Blick erkennbar ist: das ist keine echte
                    Analystenschaetzung mehr. Ganz normal klickbar (siehe handleChartClick). */}
                {showFairValue && (
                  <Line
                    type="linear"
                    dataKey="fairValueExtrapolated"
                    stroke={fairValueColor}
                    strokeOpacity={0.6}
                    strokeWidth={2}
                    strokeDasharray="2 3"
                    dot={false}
                    activeDot={
                      hoveredKey === "fairValueExtrapolated"
                        ? { r: 4, fill: fairValueColor, fillOpacity: 0.6, stroke: "#FAF9F5", strokeWidth: 2 }
                        : false
                    }
                    isAnimationActive={false}
                    connectNulls
                  />
                )}
                {/* Hilfslinien fuer alternative Fair-Value-Annahmen (Nutzer-Feedback
                    2026-07-18) - bewusst duenner/blasser als die Haupt-Fair-Value-Linie,
                    damit sie als "Hilfe", nicht als gleichrangige zweite Aussage wirken.
                    Trotzdem ganz normal klickbar (siehe handleChartClick oben). */}
                {showAlternatives && altMultiples && (
                  <>
                    <Line
                      type="linear"
                      dataKey="fairValueConservative"
                      stroke={fairValueColor}
                      strokeOpacity={0.5}
                      strokeWidth={1.5}
                      strokeDasharray="2 3"
                      dot={false}
                      activeDot={
                        hoveredKey === "fairValueConservative"
                          ? { r: 4, fill: fairValueColor, fillOpacity: 0.5, stroke: "#FAF9F5", strokeWidth: 2 }
                          : false
                      }
                      isAnimationActive={false}
                      connectNulls
                    />
                    <Line
                      type="linear"
                      dataKey="fairValueOptimistic"
                      stroke={fairValueColor}
                      strokeOpacity={0.5}
                      strokeWidth={1.5}
                      strokeDasharray="2 3"
                      dot={false}
                      activeDot={
                        hoveredKey === "fairValueOptimistic"
                          ? { r: 4, fill: fairValueColor, fillOpacity: 0.5, stroke: "#FAF9F5", strokeWidth: 2 }
                          : false
                      }
                      isAnimationActive={false}
                      connectNulls
                    />
                  </>
                )}
                {selection.a && selection.b && (
                  <ReferenceLine
                    segment={[
                      { x: selection.a.time, y: selection.a.value },
                      { x: selection.b.time, y: selection.b.value },
                    ]}
                    stroke={NAVY}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />
                )}
                {selection.a && (
                  <ReferenceDot x={selection.a.time} y={selection.a.value} r={5} fill={POSITIVE} stroke="#FAF9F5" strokeWidth={2} />
                )}
                {selection.b && (
                  <ReferenceDot x={selection.b.time} y={selection.b.value} r={5} fill={NAVY} stroke="#FAF9F5" strokeWidth={2} />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Gekuerzt (backlog.md Punkt 12.4, "liest laut Erik niemand") - die Klick-
              Anleitung stand hier doppelt (schon im Hinweis-Chip oberhalb des Charts) und
              ist raus; Rest auf die reine Formel + was die grafischen Elemente bedeuten
              verdichtet, deutlich kuerzer als zuvor. */}
          <p className="mt-2 max-w-xl text-xs text-muted-foreground">
            Fair Value = {metric === "eps" ? "Gewinn je Aktie" : "Umsatz je Aktie"} × Ø-
            {metric === "eps" ? "KGV" : "KUV"}
            {customMultipleOverride !== undefined ? (
              <> (eigene Annahme: {formatRatio(customMultipleOverride)} statt des historischen Ø von{" "}
              {formatRatio(fairValue.averagePe)})</>
            ) : (
              <> der letzten {fairValue.yearsUsed} Jahre ({formatRatio(fairValue.averagePe)})</>
            )}
            . Graue Zone: {forecastZoneDescription}.
            {extrapolatedForecast.length > 0 &&
              ` Fein gestrichelt: wachstumsbasierte Extrapolation über die echten Analystenschätzjahre hinaus (${formatPercent(longTermGrowthRate ?? 0)} p. a. langfristige Wachstumsrate) - keine echte Analystenschätzung mehr.`}
            {forecastBand.length > 0 && !logScale && " Goldenes Band: Analysten-Schätzbandbreite."}
            {altMultiples &&
              ` Dünnere Hilfslinien: fairer Wert bei niedrigstem/höchstem historischen ${
                metric === "eps" ? "KGV" : "KUV"
              } statt des Durchschnitts.`}{" "}
            Rein deskriptiv, keine Kursprognose.
          </p>
        </div>

        {/* Panel entrümpelt (backlog.md Punkt 12.3, "zu voll/unruhig, deutlich
            minimalistischer") - gap-6 auf gap-5 verengt, die beiden immer sichtbaren
            Erklaertext-Zeilen im Aktienkauf-Abschnitt unten sind jetzt Info-Tooltips statt
            dauerhaftem Fliesstext, gleiches Muster wie die bereits bestehenden Multiples-/
            Zeitraum-Vergleich-Tooltips. */}
        <div className="flex flex-col gap-5">
          <div>
            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-ink">
              Multiples
              <InfoTooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="Erklärung: Multiples" className="text-primary hover:text-primary/80">
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-64">
                  Historischer Durchschnitt (im gewählten Bewertungszeitraum) vs. aktueller Wert des
                  Kurs-Gewinn- bzw. Kurs-Umsatz-Verhältnisses.
                </TooltipContent>
              </InfoTooltip>
            </h4>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th className="text-left font-normal">Kennzahl</th>
                  <th className="text-right font-normal">Schnitt</th>
                  <th className="text-right font-normal">Akt.</th>
                </tr>
              </thead>
              <tbody>
                {avgPe !== undefined && (
                  <tr>
                    <td className="py-1 text-ink">KGV</td>
                    <td className="py-1 text-right font-tabular text-ink">{formatRatio(avgPe)}</td>
                    <td className="py-1 text-right font-tabular text-ink">{formatRatio(currentPe)}</td>
                  </tr>
                )}
                {avgPriceToSales !== undefined && (
                  <tr>
                    <td className="py-1 text-ink">KUV</td>
                    <td className="py-1 text-right font-tabular text-ink">{formatRatio(avgPriceToSales)}</td>
                    <td className="py-1 text-right font-tabular text-ink">{formatRatio(currentPriceToSales)}</td>
                  </tr>
                )}
                {currentEvToEbitda !== undefined && (
                  <tr>
                    <td className="py-1 text-ink">EV/EBITDA</td>
                    <td className="py-1 text-right font-tabular text-muted-foreground">
                      keine Historie
                    </td>
                    <td className="py-1 text-right font-tabular text-ink">{formatRatio(currentEvToEbitda)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <h4 className="flex items-center gap-1.5 text-sm font-semibold text-ink">
                Zeitraum-Vergleich
                <InfoTooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="Erklärung: Zeitraum-Vergleich"
                      className="text-primary hover:text-primary/80"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-64">
                    Klicke zwei Punkte im Chart an - auf dem echten Kurs, der Fair-Value-Linie oder
                    einer der beiden dünneren Hilfslinien (konservativere/optimistischere Annahme).
                  </TooltipContent>
                </InfoTooltip>
              </h4>
              {(selection.a || selection.b) && (
                <button
                  type="button"
                  onClick={resetSelection}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-ink"
                >
                  <RotateCcw className="h-3 w-3" /> Zurücksetzen
                </button>
              )}
            </div>
            {!selection.a ? (
              <p className="text-xs text-muted-foreground">
                Klicke zwei Punkte auf dem Chart an, um einen beliebigen Zeitraum zu markieren.
              </p>
            ) : !selection.b ? (
              <p className="text-xs text-muted-foreground">
                Startpunkt {formatTooltipDate(selection.a.time)} ({SOURCE_LABELS[selection.a.source]}) gesetzt – klicke
                einen zweiten Punkt.
              </p>
            ) : !intervalResult ? (
              <p className="text-xs text-muted-foreground">Für diesen Zeitraum liegen keine ausreichenden Daten vor.</p>
            ) : (
              <div className="flex flex-col gap-2 text-sm">
                <p className="mb-1 text-xs text-muted-foreground">
                  {formatTooltipDate(intervalResult.startDate.getTime())}
                  {intervalSources && ` (${SOURCE_LABELS[intervalSources.start]})`} –{" "}
                  {formatTooltipDate(intervalResult.endDate.getTime())}
                  {intervalSources && ` (${SOURCE_LABELS[intervalSources.end]})`}
                </p>
                <Row
                  label="Performance"
                  value={formatPercent(intervalResult.priceChangePct)}
                  tone={intervalResult.priceChangePct >= 0 ? "positive" : "negative"}
                />
                <Row
                  label="Performance p. a."
                  value={
                    intervalResult.annualizedReturnPct !== undefined
                      ? formatPercent(intervalResult.annualizedReturnPct)
                      : "–"
                  }
                  tone={
                    intervalResult.annualizedReturnPct !== undefined && intervalResult.annualizedReturnPct >= 0
                      ? "positive"
                      : "negative"
                  }
                />
                <Row label="Dividende/Aktie" value={formatCurrency(intervalResult.dividendPerShare, currency)} tone="positive" />
                <Row
                  label="Pers. Div.-Rendite p. a."
                  value={
                    intervalResult.personalDividendYieldOnCostAnnualized !== undefined
                      ? formatPercent(intervalResult.personalDividendYieldOnCostAnnualized)
                      : "–"
                  }
                  tooltip="Ø Dividendenrendite über den GESAMTEN gewählten Zeitraum, bezogen auf den Kurs/Wert am früheren der beiden Punkte (Einstandspreis) – keine aktuelle/heutige Rendite. War die Dividende am Anfang des Zeitraums niedriger, drückt das diesen Durchschnitt, auch wenn die Dividende seither gestiegen ist."
                />
              </div>
            )}
          </div>

          <div>
            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-ink">
              Aktienkauf
              <InfoTooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="Erklärung: Aktienkauf" className="text-primary hover:text-primary/80">
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-64">
                  Rechnerisches Szenario auf Basis deiner Annahmen – keine Aussage über die Zukunft.
                </TooltipContent>
              </InfoTooltip>
            </h4>
            <label className="mb-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
              Betrag ({currency})
              <Input
                type="number"
                min={0}
                value={amountDraft}
                onChange={(e) => setAmountDraft(e.target.value)}
                className="w-28 bg-card text-right"
              />
            </label>

            {!lastForecastPoint || currentPrice === undefined ? (
              <p className="text-xs text-muted-foreground">
                Für ein Vorwärts-Szenario werden Analystenschätzungen benötigt, die für diese Aktie
                aktuell nicht vorliegen.
              </p>
            ) : !simulation ? (
              <p className="text-xs text-muted-foreground">Bitte einen gültigen Betrag eingeben.</p>
            ) : (
              <div className="flex flex-col gap-2 text-sm">
                <Row label="Aktien" value={formatRatio(simulation.shares, 2)} />
                <Row
                  label="Kursgewinn"
                  value={formatCurrency(simulation.priceGain, currency)}
                  tone={simulation.priceGain >= 0 ? "positive" : "negative"}
                />
                <Row label="Dividende" value={formatCurrency(simulation.dividendIncome, currency)} tone="positive" />
                <Row
                  label="Gesamt"
                  value={formatCurrency(simulation.total, currency)}
                  tone={simulation.total >= 0 ? "positive" : "negative"}
                />
                <Row
                  label="Rendite gesamt"
                  value={formatPercent(simulation.totalReturnPct)}
                  tone={simulation.totalReturnPct >= 0 ? "positive" : "negative"}
                />
                <Row
                  label="Rendite p. a."
                  value={simulation.annualizedReturnPct !== undefined ? formatPercent(simulation.annualizedReturnPct) : "–"}
                  tone={
                    simulation.annualizedReturnPct !== undefined && simulation.annualizedReturnPct >= 0
                      ? "positive"
                      : "negative"
                  }
                />
                <Row
                  label="Pers. Div.-Rendite p. a."
                  value={
                    simulation.personalDividendYieldOnCostAnnualized !== undefined
                      ? formatPercent(simulation.personalDividendYieldOnCostAnnualized)
                      : "–"
                  }
                  tooltip="Ø Dividendenrendite über den GESAMTEN Simulationszeitraum, bezogen auf den heutigen Einstandspreis – keine aktuelle/heutige Rendite."
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Bis {lastForecastPoint.year}, entlang Fair Value. Dividende:{" "}
                  {formatCurrency(latestDividend, currency)}/Jahr, ohne Wachstumsannahme.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  tone,
  tooltip,
}: {
  label: string;
  value: string;
  tone?: "positive" | "negative";
  /** Optionale Erklaerung als Info-Icon neben dem Label (Feedback-Runde 19 B/16,
   * Blackstone-Meldung "Pers. Div.-Rendite p. a. zu niedrig") - fuer Kennzahlen, deren
   * Name allein missverstanden werden kann (hier: Durchschnitt ueber den gewaehlten
   * Zeitraum, keine "heutige" Rendite). Label-Text selbst bewusst NICHT veraendert,
   * siehe Breiten-Kommentar weiter oben in dieser Datei. */
  tooltip?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      {/* Erik-Feedback 2026-07-21: wirkte insgesamt "zu grau/dicht" - text-ink-secondary
          (#26407f) statt text-muted-foreground (#6b7280) fuer deutlich mehr Kontrast,
          bleibt aber bewusst blasser als der eigentliche Wert (text-ink/-positive/-negative
          unten), damit Label weiterhin klar als Label erkennbar bleibt. */}
      <span className="inline-flex items-center gap-1 text-ink-secondary">
        {label}
        {tooltip && (
          <InfoTooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label={`Erklärung: ${label}`} className="text-primary hover:text-primary/80">
                <Info className="h-3 w-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-64">{tooltip}</TooltipContent>
          </InfoTooltip>
        )}
      </span>
      <span
        className={
          tone === "positive" ? "font-tabular text-positive" : tone === "negative" ? "font-tabular text-negative" : "font-tabular text-ink"
        }
      >
        {value}
      </span>
    </div>
  );
}
